/**
 * A listagem de uma coleção do mundo.
 *
 * <p>É uma tela só para as dez coleções: o que muda vem do esquema. A busca roda em
 * memória sobre os resumos — que trazem nome e nota — porque um mundo escrito à mão
 * cabe folgado no navegador, e consultar o servidor a cada tecla só somaria espera.
 * Se um dia a lista passar de alguns milhares, isto vira busca paginada no backend.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  CATEGORIAS_DE_ITEM,
  COLECOES,
  COR_DA_RARIDADE,
  definicaoDe,
  PALETA_DO_MAPA,
  RARIDADES_DA_CARTA,
  RARIDADES_DO_ITEM,
} from './esquema';
import { agrupaItens, filtraItens } from './agrupaItens';
import { FILTROS_VAZIOS, gravaFiltros, leFiltros } from './filtrosDeItem';
import { destinoDa } from '../../shell/menu';
import { TituloDaPagina } from '../../shell/TituloDaPagina';
import { GerenciadorDeTipos } from './GerenciadorDeTipos';
import { IconeDeRegiao } from './IconeDeRegiao';
import { CapaAutenticada } from '../../comum/ImagemAutenticada';
import * as mundoApi from './mundoApi';
import type { Colecao, Resumo } from './tipos';
import './mundo.css';

/**
 * As coleções que ganham card grande.
 *
 * <p>O critério é <b>quantas fichas a coleção vai ter</b>, e não o gosto: NPCs,
 * criaturas e itens chegam às dezenas e às centenas, e ali o card pequeno é o que
 * permite varrer a lista de relance. Estas quatro têm poucas por definição — dez raças,
 * nove facções —, e no card pequeno meia dúzia delas flutua numa tela vazia, com a arte
 * reduzida a um selo do qual não se reconhece nada.
 *
 * <p>Regiões não entram porque já têm desenho próprio (`.mu-card.regiao`, com a capa em
 * cima); Áreas, Locais, Itens e Cartas também não, porque nem passam por esta grade.
 */
const COLECOES_AMPLAS = new Set<Colecao>(['racas', 'faccoes', 'quests', 'eventos']);

export function MundoLista() {
  const { colecao } = useParams<{ colecao: Colecao }>();
  const id = (colecao ?? 'regioes') as Colecao;
  const definicao = definicaoDe(id);
  const navegar = useNavigate();
  const fila = useQueryClient();

  /*
   * Os filtros de Itens nascem do que ficou gravado; os das outras coleções nascem
   * vazios. O `useState` com função roda uma vez, na montagem — ler o localStorage a
   * cada render custaria uma leitura síncrona de disco por tecla digitada.
   */
  const inicial = id === 'itens' ? leFiltros() : FILTROS_VAZIOS;

  const [busca, setBusca] = useState(inicial.busca);

  /** O baralho e os itens filtram por raridade; nulo é "todas", vazio é "sem". */
  const [raridade, setRaridade] = useState<string | null>(inicial.raridade);

  /** Só os itens filtram por região; nulo é "todas", zero é "sem região". */
  const [regiao, setRegiao] = useState<number | null>(inicial.regiao);

  /*
   * Trocar de coleção troca os filtros: Itens recupera os seus, o resto zera.
   *
   * É a mesma tela para as dez coleções, então o estado sobreviveria à navegação sem
   * isto — e "Épica", que só existe no baralho, esconderia todos os itens ao se ir de
   * Cartas para Itens. A troca resolve os dois lados de uma vez: o vocabulário errado
   * não vaza, e quem volta para Itens reencontra o recorte que montou.
   */
  useEffect(() => {
    const alvo = id === 'itens' ? leFiltros() : FILTROS_VAZIOS;
    setBusca(alvo.busca);
    setRaridade(alvo.raridade);
    setRegiao(alvo.regiao);
  }, [id]);

  /*
   * Grava a cada mudança, e só em Itens.
   *
   * Sem `debounce` de propósito: `localStorage.setItem` de um objeto de três campos é
   * da ordem de microssegundos, e um `setTimeout` por tecla traria o risco de a última
   * letra não chegar ao disco quando a pessoa navega logo em seguida — que é
   * exatamente o momento que este arquivo existe para cobrir.
   */
  useEffect(() => {
    if (id !== 'itens') return;
    gravaFiltros({ busca, raridade, regiao });
  }, [id, busca, raridade, regiao]);

  const { data: fichas, isLoading, error } = useQuery<Resumo[]>({
    queryKey: ['mundo', id],
    queryFn: () => mundoApi.listar(id),
  });

  const criar = useMutation({
    mutationFn: (corpo: Record<string, unknown>) => mundoApi.criar(id, corpo),
    onSuccess: async (nova) => {
      await fila.invalidateQueries({ queryKey: ['mundo', id] });
      navegar(`/mundo/${id}/${nova.id}`);
    },
  });

  const visiveis = useMemo(() => {
    // Itens têm recorte próprio: a busca deles olha também os rótulos de categoria e
    // subcategoria, que é por onde o time procura numa lista agrupada. Fica em
    // `agrupaItens`, com teste, porque é a regra que quebra calada.
    if (id === 'itens') return filtraItens(fichas ?? [], busca, raridade, regiao);

    const termo = busca.trim().toLowerCase();
    return (fichas ?? []).filter((f) => {
      // A raridade da carta chega no `grupo` do resumo, e não no `raridade` — o baralho
      // é anterior ao campo e agrupa por ela.
      if (id === 'cartas' && raridade != null && (f.grupo ?? '') !== raridade) return false;
      if (!termo) return true;
      return (
        f.nome.toLowerCase().includes(termo) || (f.nota ?? '').toLowerCase().includes(termo)
      );
    });
  }, [fichas, busca, id, raridade, regiao]);

  function novaFicha() {
    const nome = window.prompt(`Nome da nova ${definicao.singular}:`);
    if (nome && nome.trim()) criar.mutate({ nome: nome.trim() });
  }

  // Região e Área ocupam um hexágono, então nascem com uma posição. O caminho natural
  // é criá-las clicando no mapa; este formulário existe para quem já está na listagem
  // e não quer dar a volta.
  const posicionada = id === 'regioes' || id === 'areas';
  const [novaPosicionada, setNovaPosicionada] = useState(false);
  const [gerenciandoTipos, setGerenciandoTipos] = useState(false);

  return (
    <div className="mundo">
      {/* O nome e o ícone vêm do menu quando a coleção tem item lá. Regiões e Tipos de
          local não têm — para elas vale o rótulo do esquema, que é o mesmo texto. */}
      <TituloDaPagina
        rotulo={destinoDa(`/mundo/${id}`) ? undefined : definicao.rotulo}
        acoes={
          <>
            {/* Tipo de local não tem item no menu: ele só significa alguma coisa aqui
                dentro, e é daqui que se cria, renomeia e exclui. */}
            {id === 'locais' && (
              <button className="mu-btn ghost" onClick={() => setGerenciandoTipos(true)}>
                ⬚ Tipos de local
              </button>
            )}

            {/* O mundo tem exatamente 7 regiões. Com as sete criadas, este botão só
                poderia dar erro — e botão que nunca funciona é pior que botão nenhum. */}
            {!(id === 'regioes' && (fichas ?? []).length >= 7) && (
              <button
                className="mu-btn"
                onClick={() => (posicionada ? setNovaPosicionada(true) : novaFicha())}
                disabled={criar.isPending}
              >
                + Nova {definicao.singular}
              </button>
            )}
          </>
        }
      >
        {definicao.descricao}
      </TituloDaPagina>

      {gerenciandoTipos && (
        <GerenciadorDeTipos aoFechar={() => setGerenciandoTipos(false)} />
      )}

      {novaPosicionada && (
        <NovaPosicionada
          colecao={id}
          aoFechar={() => setNovaPosicionada(false)}
          aoCriar={(corpo) => {
            setNovaPosicionada(false);
            criar.mutate(corpo);
          }}
        />
      )}

      {/*
        Na listagem de Itens o topo é congelado, e nas outras não.
        É onde a diferença aparece: as demais coleções são uma grade que se rola de uma
        vez, e os Itens são oito categorias com subcategorias dentro. Rolar até o fim das
        Receitas e ter de voltar ao topo para mudar o filtro é o percurso que a sanfona
        existe para encurtar — deixar a busca para trás desfaria metade do ganho.
      */}
      <div className={`mu-barra${id === 'itens' ? ' congelada' : ''}`}>
        <div className="mu-filtros">
          <input
            className="mu-busca"
            value={busca}
            placeholder={`Buscar em ${definicao.rotulo.toLowerCase()}…`}
            onChange={(e) => setBusca(e.target.value)}
          />
          <span className="mu-conta">
            {visiveis.length}
            {visiveis.length !== (fichas ?? []).length && ` de ${(fichas ?? []).length}`}
          </span>
        </div>

        {/* O filtro de raridade fica acima da grade, e não dentro da busca: raridade é
            uma escolha de uma lista curta e fechada, e ver as cinco de uma vez (com
            quantas fichas há em cada) responde mais do que um campo de texto. */}
        {(id === 'cartas' || id === 'itens') &&
          !isLoading &&
          !error &&
          (fichas ?? []).length > 0 && (
            <FiltroDeRaridade
              fichas={fichas ?? []}
              // As duas listas divergem, e isso está documentado no esquema: a carta diz
              // "Rara", o item diz "Raro". Cada tela mostra o vocabulário das suas fichas
              // — mostrar o da outra daria um filtro que não acha nada.
              valores={id === 'cartas' ? RARIDADES_DA_CARTA : RARIDADES_DO_ITEM}
              raridadeDe={(f) => (id === 'cartas' ? f.grupo : f.raridade) ?? ''}
              escolhida={raridade}
              aoEscolher={setRaridade}
            />
          )}

        {/* A região vem depois da raridade porque é o recorte mais grosso: primeiro
            "onde", depois "quão raro". Só em Itens — é a única coleção cujo resumo
            traz as regiões. */}
        {id === 'itens' && !isLoading && !error && (fichas ?? []).length > 0 && (
          <FiltroDeRegiao itens={fichas ?? []} escolhida={regiao} aoEscolher={setRegiao} />
        )}
      </div>

      {isLoading && <div className="mu-nada">Carregando…</div>}
      {error && (
        <div className="mu-nada erro">
          {error instanceof Error ? error.message : 'Falha ao carregar.'}
        </div>
      )}

      {!isLoading && !error && visiveis.length === 0 && (
        <div className="mu-nada">
          {(fichas ?? []).length === 0
            ? `Nenhuma ficha em ${definicao.rotulo.toLowerCase()} ainda.`
            : 'Nada corresponde ao filtro.'}
        </div>
      )}

      {/* As áreas são as sete de cada região: a listagem espelha o mapa, e é por isso
          que ela mostra também os hexágonos vazios. */}
      {id === 'areas' && !isLoading && <AreasPorRegiao busca={busca} />}

      {/* Locais não têm slot, mas têm área, e a área tem região — a listagem desce os
          três níveis, com a mesma divisória colorida do resto da seção. */}
      {id === 'locais' && !isLoading && (
        <LocaisPorRegiao locais={visiveis} filtrando={busca.trim().length > 0} />
      )}

      {/* Itens se agrupam pela categoria e pela subcategoria, em sanfonas. */}
      {id === 'itens' && !isLoading && (
        <ItensPorCategoria
          itens={visiveis}
          filtrando={busca.trim().length > 0 || raridade != null || regiao != null}
        />
      )}

      {/* A carta é um objeto que já tem uma cara: a grade mostra a arte inteira. */}
      {id === 'cartas' && !isLoading && <CartasEmGrade cartas={visiveis} />}

      {id !== 'areas' && id !== 'locais' && id !== 'itens' && id !== 'cartas' && (
        <div
          className={`mu-grade${id === 'regioes' ? ' regioes' : ''}${
            COLECOES_AMPLAS.has(id) ? ' amplo' : ''
          }`}
        >
          {visiveis.map((ficha) => (
            <button
              key={ficha.id}
              className={`mu-card${id === 'regioes' ? ' regiao' : ''}${
                COLECOES_AMPLAS.has(id) ? ' amplo' : ''
              }`}
              onClick={() => navegar(`/mundo/${id}/${ficha.id}`)}
            >
              <CapaAutenticada className="mu-capa" url={ficha.imagem}>
                {/* Sem foto, a região se apresenta pelo seu ícone, na sua cor — é o
                    que permite achá-la na lista sem ler os nomes. */}
                {!ficha.imagem &&
                  (id === 'regioes' ? (
                    <IconeDeRegiao
                      icone={ficha.icone}
                      cor={ficha.cor}
                      tamanho={72}
                      titulo={ficha.nome}
                    />
                  ) : (
                    <span className="mu-capa-ic">{definicao.icone}</span>
                  ))}
              </CapaAutenticada>
              <span className="mu-card-txt">
                <span className="mu-nome">{ficha.nome}</span>
                {ficha.nota && <span className="mu-nota">{ficha.nota}</span>}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * O ícone escolhido para cada região, por id.
 *
 * <p>O endpoint do mapa desenha os hexágonos e não carrega essa escolha; ela vem da
 * listagem de regiões, que é barata e já está em cache quando o time navega pela seção.
 */
function useIconesDeRegiao() {
  const { data: regioes } = useQuery<Resumo[]>({
    queryKey: ['mundo', 'regioes'],
    queryFn: () => mundoApi.listar('regioes'),
  });
  return useMemo(() => {
    const porId = new Map<number, string | undefined>();
    (regioes ?? []).forEach((r) => porId.set(r.id, r.icone));
    return porId;
  }, [regioes]);
}

/**
 * As áreas agrupadas pelas 7 regiões, 7 colunas cada — a mesma forma do mapa.
 *
 * <p>Lê do endpoint do mapa, e não da listagem simples, porque só ele conhece os
 * <b>slots</b>: quais dos sete hexágonos de cada região estão ocupados e quais estão
 * vazios. É isso que faz a tela mostrar o que <i>falta</i>, e não só o que existe —
 * uma lista de duas áreas não diz que faltam doze.
 *
 * <p>As imagens vêm da listagem, casadas por id: o mapa devolve só o que ele desenha.
 */
function AreasPorRegiao({ busca }: { busca: string }) {
  const navegar = useNavigate();
  const fila = useQueryClient();

  const { data: mapa } = useQuery({ queryKey: ['mundo', 'mapa'], queryFn: mundoApi.carregarMapa });
  const { data: areas } = useQuery<Resumo[]>({
    queryKey: ['mundo', 'areas'],
    queryFn: () => mundoApi.listar('areas'),
  });

  const imagemPorId = useMemo(() => {
    const mapaDeImagens = new Map<number, string>();
    (areas ?? []).forEach((a) => a.imagem && mapaDeImagens.set(a.id, a.imagem));
    return mapaDeImagens;
  }, [areas]);

  const iconePorRegiao = useIconesDeRegiao();

  const criar = useMutation({
    mutationFn: (v: { nome: string; indiceMapa: number; regiaoId: number }) =>
      mundoApi.criar('areas', v),
    onSuccess: async (nova) => {
      await fila.invalidateQueries({ queryKey: ['mundo'] });
      navegar(`/mundo/areas/${nova.id}`);
    },
  });

  const termo = busca.trim().toLowerCase();

  return (
    <div className="ar-regioes">
      {(mapa?.regioes ?? []).map((regiao, i) => {
        const cor = regiao.corMapa || PALETA_DO_MAPA[i % PALETA_DO_MAPA.length];
        return (
          <section key={regiao.indiceMapa} className="ar-bloco">
            <header
              className="ar-cab"
              style={{ '--c': regiao.id ? cor : 'var(--linha)' } as React.CSSProperties}
            >
              <IconeDeRegiao icone={iconePorRegiao.get(regiao.id ?? -1)} cor={cor} tamanho={20} />
              {regiao.id ? (
                <button className="ar-nome" onClick={() => navegar(`/mundo/regioes/${regiao.id}`)}>
                  {regiao.nome}
                </button>
              ) : (
                <span className="ar-nome vazia">Região {regiao.indiceMapa + 1}</span>
              )}
              <span className="ar-conta">
                {regiao.hexagonos.filter((h) => h.areaId).length} de 7
              </span>
            </header>

            <div className="ar-linha">
              {regiao.hexagonos.map((hex) => {
                const escondida = termo && hex.nome && !hex.nome.toLowerCase().includes(termo);
                if (hex.areaId) {
                  const imagem = imagemPorId.get(hex.areaId);
                  return (
                    <button
                      key={hex.indiceMapa}
                      className={`ar-cel${escondida ? ' apagada' : ''}`}
                      onClick={() => navegar(`/mundo/areas/${hex.areaId}`)}
                      title={hex.nome}
                    >
                      <CapaAutenticada className="ar-capa" url={imagem}>
                        {!imagem && <span className="mu-capa-ic">◆</span>}
                      </CapaAutenticada>
                      <span className="ar-cel-nome">{hex.nome}</span>
                    </button>
                  );
                }
                return (
                  <button
                    key={hex.indiceMapa}
                    className="ar-cel vaga"
                    disabled={!regiao.id}
                    title={
                      regiao.id
                        ? `Criar a área ${hex.indiceMapa + 1} de ${regiao.nome}`
                        : 'Crie a região primeiro, no mapa'
                    }
                    onClick={() => {
                      const nome = window.prompt(`Nome da nova área em "${regiao.nome}":`);
                      if (nome?.trim()) {
                        criar.mutate({
                          nome: nome.trim(),
                          indiceMapa: hex.indiceMapa,
                          regiaoId: regiao.id!,
                        });
                      }
                    }}
                  >
                    <span className="ar-mais">+</span>
                  </button>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}

/** Um card de local, do tamanho de sempre — o mesmo em qualquer bloco desta tela. */
function CardDeLocal({ local, dentro = 0 }: { local: Resumo; dentro?: number }) {
  const navegar = useNavigate();
  return (
    <button className="mu-card" onClick={() => navegar(`/mundo/locais/${local.id}`)}>
      <CapaAutenticada className="mu-capa" url={local.imagem}>
        {!local.imagem && <span className="mu-capa-ic">⌂</span>}
      </CapaAutenticada>
      <span className="mu-card-txt">
        <span className="mu-nome">{local.nome}</span>
        {dentro > 0 && (
          <span className="mu-nota">
            {dentro} {dentro === 1 ? 'local dentro' : 'locais dentro'}
          </span>
        )}
      </span>
    </button>
  );
}

/** Um local com o que está dentro dele. */
interface Ninho {
  local: Resumo;
  dentro: Ninho[];
}

/**
 * Monta a árvore de locais a partir do {@code dentroDe} de cada um.
 *
 * <p>Quem tem pai fora da lista sobe para a raiz em vez de sumir. Isso acontece em dois
 * casos reais: o pai não passou no filtro da busca, e o pai é o local padrão da área —
 * que existe, mas nunca se lista.
 *
 * <p>Não há defesa contra ciclo aqui porque não pode haver ciclo: o backend recusa pôr um
 * local dentro de um descendente seu, e `MundoVinculosTest` cobra isso.
 */
function aninhar(locais: Resumo[]): Ninho[] {
  const nos = new Map<number, Ninho>(locais.map((l) => [l.id, { local: l, dentro: [] }]));
  const raizes: Ninho[] = [];
  locais.forEach((l) => {
    const pai = l.dentroDe == null ? undefined : nos.get(l.dentroDe);
    if (pai) pai.dentro.push(nos.get(l.id)!);
    else raizes.push(nos.get(l.id)!);
  });
  return raizes;
}

/**
 * Os locais de uma área, com os que moram dentro de outros logo abaixo deles.
 *
 * <p>Uma corrida de locais sem nada dentro entra numa grade só, compacta; quem abriga
 * outros ganha o seu próprio galho, na posição em que já estava. Assim a ordem alfabética
 * não se perde e o caso comum — o local raso — continua ocupando uma linha por quatro.
 */
function ArvoreDeLocais({ ninhos }: { ninhos: Ninho[] }) {
  const blocos: (Resumo[] | Ninho)[] = [];
  let corrida: Resumo[] = [];
  ninhos.forEach((n) => {
    if (n.dentro.length === 0) {
      corrida.push(n.local);
      return;
    }
    if (corrida.length > 0) blocos.push(corrida);
    corrida = [];
    blocos.push(n);
  });
  if (corrida.length > 0) blocos.push(corrida);

  return (
    <>
      {blocos.map((bloco, i) =>
        Array.isArray(bloco) ? (
          <div key={`rasos-${i}`} className="mu-grade">
            {bloco.map((l) => (
              <CardDeLocal key={l.id} local={l} />
            ))}
          </div>
        ) : (
          <div key={bloco.local.id} className="lo-galho">
            <div className="mu-grade">
              <CardDeLocal local={bloco.local} dentro={bloco.dentro.length} />
            </div>
            <div className="lo-dentro">
              <ArvoreDeLocais ninhos={bloco.dentro} />
            </div>
          </div>
        )
      )}
    </>
  );
}

/**
 * Os locais na hierarquia em que eles existem: Região › Área › Local › local dentro dele.
 *
 * <p>Um local pertence a uma área, a área é que pertence a uma região, e um local ainda
 * pode estar dentro de outro — parar no primeiro nível juntava numa lista corrida os
 * locais de sete áreas diferentes, com o quarto da taverna ao lado da taverna. A pergunta
 * que o time faz ("o que já existe na Vila do Norte?") não tinha resposta na tela. Aqui
 * cada área é um sub-bloco com o seu nome e a sua conta, e cada local que abriga outros
 * abre o seu galho.
 *
 * <p>Só aparecem as áreas que <b>têm</b> local; as outras viram uma linha de atalhos no
 * pé da região. São 49 áreas no mundo, e um sub-bloco vazio para cada uma afogaria as
 * que têm conteúdo — mas some-las de vez esconderia justamente o que falta escrever.
 * Com filtro ativo essa linha não aparece: ela fala do mundo inteiro, não da busca.
 *
 * <p>O agrupamento casa pelo <b>id</b> da área, que vem no {@code grupo} do resumo. Casar
 * pelo nome quebraria no dia em que duas regiões tivessem uma área de mesmo nome.
 *
 * <p>Locais sem área ficam num bloco à parte, no fim: são fichas escritas antes de o
 * lugar existir, e escondê-las seria perdê-las de vista.
 */
function LocaisPorRegiao({ locais, filtrando }: { locais: Resumo[]; filtrando: boolean }) {
  const navegar = useNavigate();
  const { data: mapa } = useQuery({ queryKey: ['mundo', 'mapa'], queryFn: mundoApi.carregarMapa });

  const iconePorRegiao = useIconesDeRegiao();

  /** Id da área → os locais dela. Quem não tem área cai em `semArea`. */
  const { porArea, semArea } = useMemo(() => {
    const porArea = new Map<number, Resumo[]>();
    const semArea: Resumo[] = [];
    locais.forEach((l) => {
      const areaId = l.grupo ? Number(l.grupo) : NaN;
      if (!Number.isFinite(areaId)) semArea.push(l);
      else porArea.set(areaId, [...(porArea.get(areaId) ?? []), l]);
    });
    return { porArea, semArea };
  }, [locais]);

  /** Os ids de área que o mapa desenha — o que sobra é órfão e vai para o fim. */
  const areasNoMapa = useMemo(() => {
    const ids = new Set<number>();
    (mapa?.regioes ?? []).forEach((r) =>
      r.hexagonos.forEach((h) => h.areaId && ids.add(h.areaId))
    );
    return ids;
  }, [mapa]);

  const orfaos = useMemo(
    () => [...porArea.entries()].filter(([id]) => !areasNoMapa.has(id)).flatMap(([, l]) => l),
    [porArea, areasNoMapa]
  );

  return (
    <div className="ar-regioes">
      {(mapa?.regioes ?? []).map((regiao, i) => {
        const areasDela = regiao.hexagonos.filter((h) => h.areaId);
        const comLocais = areasDela.filter((h) => (porArea.get(h.areaId!) ?? []).length > 0);
        const semLocais = areasDela.filter((h) => (porArea.get(h.areaId!) ?? []).length === 0);
        const quantos = comLocais.reduce((s, h) => s + (porArea.get(h.areaId!) ?? []).length, 0);

        // Região que não existe não vira bloco; e durante uma busca, região sem
        // resultado só somaria ruído entre as que têm.
        if (regiao.id == null) return null;
        if (filtrando && quantos === 0) return null;

        const cor = regiao.corMapa || PALETA_DO_MAPA[i % PALETA_DO_MAPA.length];

        return (
          <section key={regiao.indiceMapa} className="ar-bloco">
            <header className="ar-cab" style={{ '--c': cor } as React.CSSProperties}>
              <IconeDeRegiao icone={iconePorRegiao.get(regiao.id)} cor={cor} tamanho={20} />
              <button className="ar-nome" onClick={() => navegar(`/mundo/regioes/${regiao.id}`)}>
                {regiao.nome}
              </button>
              <span className="ar-conta">
                {quantos} {quantos === 1 ? 'local' : 'locais'}
                {comLocais.length > 0 &&
                  ` em ${comLocais.length} de ${areasDela.length} ${
                    areasDela.length === 1 ? 'área' : 'áreas'
                  }`}
              </span>
            </header>

            {/* Sem nenhuma área não há o que dizer pelas pastilhas: o que falta aqui é
                um nível acima, e o caminho é o mapa. */}
            {areasDela.length === 0 && (
              <div className="ar-sem">Nenhuma área criada nesta região ainda.</div>
            )}

            {comLocais.map((hex) => {
              const dela = porArea.get(hex.areaId!) ?? [];
              return (
                <div
                  key={hex.areaId}
                  className="lo-area"
                  style={{ '--c': cor } as React.CSSProperties}
                >
                  <div className="lo-area-cab">
                    {/* O losango vem do CSS (`::before`), na cor da região — aqui ele
                        seria texto preto que o leitor de tela leria como "losango". */}
                    <button
                      className="lo-area-nome"
                      onClick={() => navegar(`/mundo/areas/${hex.areaId}`)}
                    >
                      {hex.nome}
                    </button>
                    <span className="lo-area-conta">
                      {dela.length} {dela.length === 1 ? 'local' : 'locais'}
                    </span>
                  </div>
                  <ArvoreDeLocais ninhos={aninhar(dela)} />
                </div>
              );
            })}

            {!filtrando && semLocais.length > 0 && (
              <div className="lo-vazias">
                <span className="lo-vazias-rot">Sem local ainda:</span>
                {semLocais.map((hex) => (
                  <button
                    key={hex.areaId}
                    className="lo-chip"
                    title={`Abrir a área ${hex.nome}`}
                    onClick={() => navegar(`/mundo/areas/${hex.areaId}`)}
                  >
                    {hex.nome}
                  </button>
                ))}
              </div>
            )}
          </section>
        );
      })}

      {semArea.length > 0 && (
        <section className="ar-bloco">
          <header className="ar-cab">
            <span className="ar-cor" style={{ background: 'transparent' }} />
            <span className="ar-nome vazia">Sem área</span>
            <span className="ar-conta">{semArea.length}</span>
          </header>
          <ArvoreDeLocais ninhos={aninhar(semArea)} />
        </section>
      )}

      {/* Não deveria acontecer — toda área ocupa um hexágono. Se acontecer, é melhor
          ver a ficha do que perdê-la calada. */}
      {orfaos.length > 0 && (
        <section className="ar-bloco">
          <header className="ar-cab">
            <span className="ar-cor" style={{ background: 'transparent' }} />
            <span className="ar-nome vazia">Em área fora do mapa</span>
            <span className="ar-conta">{orfaos.length}</span>
          </header>
          <ArvoreDeLocais ninhos={aninhar(orfaos)} />
        </section>
      )}
    </div>
  );
}

/** Onde o navegador guarda quais sanfonas da listagem de Itens estão fechadas. */
const SANFONAS_FECHADAS = 'gg-pilot:itens:sanfonas-fechadas';

/**
 * Quais blocos estão fechados, lembrado entre visitas.
 *
 * <p><b>Guarda os fechados, e não os abertos.</b> A diferença aparece quando uma
 * categoria nova entra no esquema — como ferramenta, tesouro e item de quest entraram:
 * guardando os abertos, ela nasceria fechada para quem já usava a tela, e a novidade
 * chegaria escondida. Guardando os fechados, tudo que ninguém mandou fechar está
 * aberto, hoje e depois.
 *
 * <p>É `localStorage` e não estado de servidor porque é preferência de leitura, e do
 * navegador: a mesma pessoa pode querer as Receitas abertas no monitor grande e tudo
 * fechado no laptop. Uma coluna no banco imporia uma resposta só para os dois.
 */
function useSanfonas(): [Set<string>, (chave: string) => void] {
  const [fechados, setFechados] = useState<Set<string>>(() => {
    try {
      const salvo = window.localStorage.getItem(SANFONAS_FECHADAS);
      return new Set<string>(salvo ? (JSON.parse(salvo) as string[]) : []);
    } catch {
      // localStorage bloqueado, ou JSON corrompido por uma versão anterior. A tela
      // abre inteira, que é o padrão — preferência de leitura não vale um erro.
      return new Set<string>();
    }
  });

  function alternar(chave: string) {
    setFechados((atual) => {
      const novo = new Set(atual);
      if (novo.has(chave)) novo.delete(chave);
      else novo.add(chave);
      try {
        window.localStorage.setItem(SANFONAS_FECHADAS, JSON.stringify([...novo]));
      } catch {
        // Sem persistir, a sanfona ainda funciona nesta visita.
      }
      return novo;
    });
  }

  return [fechados, alternar];
}

/**
 * O filtro de itens por região.
 *
 * <p>As sete regiões vêm da própria coleção, e não de uma lista fixa: o mundo tem sete
 * por definição, mas quantas já foram escritas é dado. A que não tem nenhum item fica
 * desabilitada em vez de sumir — como no filtro de raridade, ver "Ferronte 0" é a
 * informação de que ali ainda não se cadastrou nada.
 *
 * <p>A contagem lê a <b>união</b> que o servidor montou: regiões marcadas à mão mais as
 * derivadas dos locais. É por isso que ela responde "distribuição" — um item colocado
 * num local de Valdris conta em Valdris sem ninguém ter marcado nada.
 */
function FiltroDeRegiao({
  itens,
  escolhida,
  aoEscolher,
}: {
  itens: Resumo[];
  escolhida: number | null;
  aoEscolher: (r: number | null) => void;
}) {
  const { data: regioes } = useQuery<Resumo[]>({
    queryKey: ['mundo', 'regioes'],
    queryFn: () => mundoApi.listar('regioes'),
    staleTime: Infinity,
  });

  const { contagem, semRegiao } = useMemo(() => {
    const contagem = new Map<number, number>();
    let semRegiao = 0;
    itens.forEach((i) => {
      const suas = i.regioes ?? [];
      if (suas.length === 0) semRegiao += 1;
      suas.forEach((r) => contagem.set(r, (contagem.get(r) ?? 0) + 1));
    });
    return { contagem, semRegiao };
  }, [itens]);

  if (!regioes || regioes.length === 0) return null;

  function pastilha(valor: number | null, rotulo: string, quantos: number, cor?: string) {
    return (
      <button
        key={rotulo}
        type="button"
        className={`ca-chip${escolhida === valor ? ' on' : ''}`}
        style={cor ? ({ '--c': cor } as React.CSSProperties) : undefined}
        disabled={quantos === 0 && valor !== null}
        onClick={() => aoEscolher(valor)}
        aria-pressed={escolhida === valor}
      >
        {cor && <span className="ca-ponto" aria-hidden="true" />}
        {rotulo}
        <span className="ca-chip-n">{quantos}</span>
      </button>
    );
  }

  return (
    <div className="ca-filtro" role="group" aria-label="Filtrar por região">
      {pastilha(null, 'Todo o mapa', itens.length)}
      {regioes.map((r) => pastilha(r.id, r.nome, contagem.get(r.id) ?? 0, r.cor))}
      {/* Zero é o balde "sem região", e não o id de nenhuma: a sequência do Postgres
          começa em 1. Só aparece quando há o que mostrar nele. */}
      {semRegiao > 0 && pastilha(0, 'Sem região', semRegiao)}
    </div>
  );
}

/**
 * A frase que explica a categoria, editável na própria listagem.
 *
 * <p>Fica <b>abaixo</b> do cabeçalho, e não dentro dele, por duas razões. A primeira é
 * de marcação: o cabeçalho é um {@code <button>}, e campo de texto dentro de botão é
 * HTML inválido — o clique de um comeria o do outro. A segunda é de leitura: estas
 * frases são sentenças inteiras ("recursos de valor comercial ou histórico, podem ter
 * efeitos em quests…"), e espremê-las na mesma linha do título as reduziria a
 * reticências justamente onde elas explicam.
 *
 * <p>Continua visível com a sanfona fechada, de propósito: com tudo recolhido, a tela
 * vira o índice das categorias, e é aí que saber o que cada uma quer dizer vale mais.
 *
 * <p>Grava ao sair do campo, sem botão salvar — a mesma regra da ficha, e pela mesma
 * razão: isto é documento vivo, e botão esquecido é trabalho perdido.
 */
function DescricaoDaCategoria({
  categoria,
  texto,
  aoSalvar,
}: {
  categoria: string;
  texto: string;
  aoSalvar: (categoria: string, descricao: string) => void;
}) {
  const [editando, setEditando] = useState(false);

  if (!editando) {
    return (
      <button
        type="button"
        className={`sf-desc${texto ? '' : ' vazia'}`}
        title="Clique para editar"
        onClick={() => setEditando(true)}
      >
        {texto || 'Escrever o que esta categoria quer dizer…'}
      </button>
    );
  }

  return (
    <textarea
      className="sf-desc-campo"
      defaultValue={texto}
      autoFocus
      rows={2}
      placeholder="O que esta categoria quer dizer?"
      onBlur={(e) => {
        setEditando(false);
        const novo = e.target.value.trim();
        // Só chama a API se mudou: sair do campo sem digitar nada é o caso comum de
        // quem clicou por engano, e não vale um PATCH nem uma linha de atividade.
        if (novo !== texto) aoSalvar(categoria, novo);
      }}
      onKeyDown={(e) => {
        // Escape desiste sem gravar. Sem ele, a única saída de um campo aberto por
        // engano seria apagar o que já estava escrito ou recarregar a página.
        if (e.key === 'Escape') {
          e.currentTarget.value = texto;
          e.currentTarget.blur();
        }
      }}
    />
  );
}

/** O cabeçalho clicável de uma sanfona: seta, título e contagem. */
function CabecaDeSanfona({
  rotulo,
  quantos,
  aberto,
  nivel,
  aoAlternar,
}: {
  rotulo: string;
  quantos: number;
  aberto: boolean;
  nivel: 'categoria' | 'sub';
  aoAlternar: () => void;
}) {
  return (
    <button
      type="button"
      className={`sf-cab ${nivel}${aberto ? ' aberto' : ''}`}
      aria-expanded={aberto}
      onClick={aoAlternar}
    >
      <span className="sf-seta" aria-hidden="true">
        ▸
      </span>
      <span className="sf-nome">{rotulo}</span>
      <span className="sf-conta">
        {quantos} {quantos === 1 ? 'item' : 'itens'}
      </span>
    </button>
  );
}

/** A grade de cards de um bloco. */
function GradeDeItens({ itens }: { itens: Resumo[] }) {
  const navegar = useNavigate();

  return (
    <div className="mu-grade">
      {itens.map((item) => (
        <button
          key={item.id}
          className={`mu-card${item.raridade ? ' com-raridade' : ''}`}
          /*
           * A cor da raridade em volta da capa, como a moldura da carta faz na ficha —
           * mesma variável, mesmo mapa de cores. Numa grade de dezenas de itens iguais
           * em tamanho, a mancha de cor responde "o que aqui é raro?" antes de qualquer
           * palavra ser lida, que é a pergunta que a lista ordenada por raridade também
           * responde: as duas dizem a mesma coisa, uma pela posição e outra pela cor.
           *
           * Sem raridade escolhida não há moldura — e não uma cor neutra qualquer:
           * "ainda não decidimos" não pode parecer uma sexta raridade.
           */
          style={
            item.raridade
              ? ({ '--raridade': COR_DA_RARIDADE[item.raridade] ?? 'var(--card-brd)' } as React.CSSProperties)
              : undefined
          }
          title={item.raridade ? `${item.nome} · ${item.raridade}` : item.nome}
          onClick={() => navegar(`/mundo/itens/${item.id}`)}
        >
          {/* `mini` porque a capa aqui tem menos de 100px de lado: baixar o original de
              cada ficha era o que fazia esta tela pesar dezenas de megabytes. */}
          <CapaAutenticada className="mu-capa" url={item.imagem} mini>
            {!item.imagem && <span className="mu-capa-ic">✦</span>}
          </CapaAutenticada>
          <span className="mu-card-txt">
            <span className="mu-nome">{item.nome}</span>
            {item.nota && (
              <span className="mu-nota">{rotuloDaSubcategoria(item.grupo, item.nota)}</span>
            )}
          </span>
        </button>
      ))}
    </div>
  );
}

/**
 * Os itens em sanfonas de categoria e, dentro delas, de subcategoria.
 *
 * <p>Numa lista corrida, arma, minério e poção se misturam e a leitura não diz nada;
 * separados, cada bloco responde a uma pergunta diferente do time. Os que ainda não
 * têm categoria ficam num bloco no fim — não escondê-los é o que faz alguém
 * categorizá-los.
 *
 * <p><b>Abrir e fechar é o que torna a tela usável com o inventário cheio.</b> Oito
 * categorias com subcategorias dentro passam de uma tela de rolagem bem antes de o
 * mundo ficar pronto, e procurar a Alquimia rolando por todas as armas é o percurso
 * que a sanfona encurta. O estado mora em {@link useSanfonas}, e não aqui, porque ele
 * precisa sobreviver a navegar até uma ficha e voltar.
 *
 * <p>O agrupamento em si mora em `agrupaItens`, com teste — é onde estão as decisões
 * que quebram sem avisar.
 */
function ItensPorCategoria({ itens, filtrando }: { itens: Resumo[]; filtrando: boolean }) {
  const [fechados, alternar] = useSanfonas();
  const fila = useQueryClient();

  const { data: descricoes } = useQuery({
    queryKey: ['mundo', 'categorias-de-item'],
    queryFn: mundoApi.carregarCategoriasDeItem,
    staleTime: Infinity,
  });

  const salvarDescricao = useMutation({
    mutationFn: ({ categoria, texto }: { categoria: string; texto: string }) =>
      mundoApi.salvarDescricaoDaCategoria(categoria, texto),
    // O PATCH devolve a lista inteira: escrever direto no cache evita o GET extra que
    // um invalidate faria, e é a mesma lista que o servidor acabou de confirmar.
    onSuccess: (lista) => fila.setQueryData(['mundo', 'categorias-de-item'], lista),
  });

  const textoDe = (categoria: string) =>
    descricoes?.find((d) => d.categoria === categoria)?.descricao ?? '';

  const blocos = useMemo(
    () => agrupaItens(itens, { escondeVazias: filtrando }),
    [itens, filtrando],
  );

  if (filtrando && blocos.length === 0) return null;

  return (
    <div className="ar-regioes">
      {blocos.map((bloco) => {
        const chave = bloco.id || 'sem-categoria';
        const aberto = !fechados.has(chave);

        return (
          <section key={chave} className="ar-bloco sanfona">
            <CabecaDeSanfona
              rotulo={bloco.rotulo}
              quantos={bloco.itens.length}
              aberto={aberto}
              nivel="categoria"
              aoAlternar={() => alternar(chave)}
            />

            {/* O balde "Sem categoria" não recebe descrição: ele não é uma categoria,
                é a ausência de uma. Explicá-lo seria dar nome ao que falta preencher. */}
            {bloco.id !== '' && (
              <DescricaoDaCategoria
                categoria={bloco.id}
                texto={textoDe(bloco.id)}
                aoSalvar={(categoria, texto) => salvarDescricao.mutate({ categoria, texto })}
              />
            )}

            {aberto && bloco.itens.length === 0 && (
              <div className="ar-sem">Nenhum item nesta categoria ainda.</div>
            )}

            {/* Categoria sem subdivisão vai direto à grade: uma sanfona única dentro da
                sanfona seria um clique a mais para chegar à mesma lista. */}
            {aberto && bloco.itens.length > 0 && bloco.subs.length === 0 && (
              <GradeDeItens itens={bloco.itens} />
            )}

            {aberto &&
              bloco.subs.map((sub) => {
                const chaveDaSub = `${chave}/${sub.id || 'sem-sub'}`;
                const subAberta = !fechados.has(chaveDaSub);
                return (
                  <div key={chaveDaSub} className="sf-sub">
                    <CabecaDeSanfona
                      rotulo={sub.rotulo}
                      quantos={sub.itens.length}
                      aberto={subAberta}
                      nivel="sub"
                      aoAlternar={() => alternar(chaveDaSub)}
                    />
                    {subAberta && <GradeDeItens itens={sub.itens} />}
                  </div>
                );
              })}
          </section>
        );
      })}
    </div>
  );
}

/**
 * O filtro do baralho por raridade.
 *
 * <p>Cada raridade traz quantas cartas tem, e a que não tem nenhuma fica desabilitada em
 * vez de sumir: a lista das cinco é fixa, e ver "Primordial 0" é a informação de que
 * ainda não se escreveu nenhuma.
 *
 * <p>Valores fora da lista ganham pastilha própria no fim — o campo é texto livre no
 * banco, e uma raridade escrita à mão não pode virar uma carta inalcançável pelo filtro.
 */
function FiltroDeRaridade({
  fichas,
  valores,
  raridadeDe,
  escolhida,
  aoEscolher,
}: {
  fichas: Resumo[];
  /** O vocabulário desta coleção — o da carta e o do item não são o mesmo. */
  valores: string[];
  /** Onde a raridade vem no resumo: no `grupo` da carta, no `raridade` do item. */
  raridadeDe: (f: Resumo) => string;
  escolhida: string | null;
  aoEscolher: (r: string | null) => void;
}) {
  const { contagem, extras } = useMemo(() => {
    const contagem = new Map<string, number>();
    fichas.forEach((f) => {
      const r = raridadeDe(f);
      contagem.set(r, (contagem.get(r) ?? 0) + 1);
    });
    const extras = [...contagem.keys()]
      .filter((r) => r !== '' && !valores.includes(r))
      .sort();
    return { contagem, extras };
    // `raridadeDe` nasce inline a cada render; incluí-lo recalcularia sempre, e o que
    // de fato muda a contagem são as fichas e a lista de valores.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fichas, valores]);

  const semRaridade = contagem.get('') ?? 0;

  function pastilha(valor: string | null, rotulo: string, quantas: number, cor?: string) {
    return (
      <button
        key={rotulo}
        type="button"
        className={`ca-chip${escolhida === valor ? ' on' : ''}`}
        style={cor ? ({ '--c': cor } as React.CSSProperties) : undefined}
        disabled={quantas === 0 && valor !== null}
        onClick={() => aoEscolher(valor)}
        aria-pressed={escolhida === valor}
      >
        {cor && <span className="ca-ponto" aria-hidden="true" />}
        {rotulo}
        <span className="ca-chip-n">{quantas}</span>
      </button>
    );
  }

  return (
    <div className="ca-filtro" role="group" aria-label="Filtrar por raridade">
      {pastilha(null, 'Todas', fichas.length)}
      {valores.map((r) => pastilha(r, r, contagem.get(r) ?? 0, COR_DA_RARIDADE[r]))}
      {extras.map((r) => pastilha(r, r, contagem.get(r) ?? 0))}
      {semRaridade > 0 && pastilha('', 'Sem raridade', semRaridade)}
    </div>
  );
}

/**
 * O baralho em grade de quatro colunas: a arte inteira e o nome, nada mais.
 *
 * <p>A carta já é um objeto desenhado — quem procura uma no baralho procura pela arte,
 * não pelo texto. Por isso ela some do card horizontal das outras coleções e vira uma
 * carta de verdade, retrato, quatro por linha.
 *
 * <p>A arte é <b>contida</b>, não recortada: a moldura, o selo e o rodapé fazem parte do
 * desenho, e cortá-los para preencher o quadro descaracterizaria a peça. O quadro tem
 * proporção fixa, então o slot vazio ocupa exatamente o espaço que a arte vai ocupar
 * quando chegar — a grade não se remexe conforme o time sobe as imagens.
 */
function CartasEmGrade({ cartas }: { cartas: Resumo[] }) {
  const navegar = useNavigate();

  return (
    <div className="ca-grade">
      {cartas.map((carta) => (
        <button
          key={carta.id}
          className="ca-carta"
          style={
            {
              '--c': COR_DA_RARIDADE[carta.grupo ?? ''] ?? 'var(--linha)',
            } as React.CSSProperties
          }
          onClick={() => navegar(`/mundo/cartas/${carta.id}`)}
          title={carta.grupo ? `${carta.nome} · ${carta.grupo}` : carta.nome}
        >
          <CapaAutenticada className="ca-arte" url={carta.imagem}>
            {!carta.imagem && <span className="ca-sem-arte">❐</span>}
          </CapaAutenticada>
          <span className="ca-nome">{carta.nome}</span>
        </button>
      ))}
    </div>
  );
}

/** O id da subcategoria vem cru da API; o rótulo bonito mora no esquema. */
function rotuloDaSubcategoria(categoria?: string, sub?: string): string {
  const dela = CATEGORIAS_DE_ITEM.find((c) => c.id === categoria);
  return dela?.sub.find((s) => s.id === sub)?.rotulo ?? sub ?? '';
}

/**
 * Criação de região e área, que precisam de um hexágono.
 *
 * <p>Provisório: o lugar certo de escolher a posição é clicando no hexágono vazio do
 * mapa. Enquanto o mapa não existe, sem isto as duas seções ficariam impossíveis de
 * usar — e sem elas o resto do mundo não tem onde se apoiar.
 */
function NovaPosicionada({
  colecao,
  aoFechar,
  aoCriar,
}: {
  colecao: Colecao;
  aoFechar: () => void;
  aoCriar: (corpo: Record<string, unknown>) => void;
}) {
  const [nome, setNome] = useState('');
  const [indiceMapa, setIndiceMapa] = useState(0);
  const [regiaoId, setRegiaoId] = useState<number | ''>('');

  const { data: regioes } = useQuery<Resumo[]>({
    queryKey: ['mundo', 'regioes'],
    queryFn: () => mundoApi.listar('regioes'),
    enabled: colecao === 'areas',
  });

  const pronto = nome.trim().length > 0 && (colecao === 'regioes' || regiaoId !== '');

  return (
    <div className="fi-modal" onClick={aoFechar}>
      <div className="fi-modal-cx" onClick={(e) => e.stopPropagation()}>
        <h3>Nova {colecao === 'regioes' ? 'região' : 'área'}</h3>

        <label className="np-rot">Nome</label>
        <input className="fi-txt" value={nome} onChange={(e) => setNome(e.target.value)} autoFocus />

        {colecao === 'areas' && (
          <>
            <label className="np-rot">Região</label>
            <select
              className="fi-sel"
              value={regiaoId}
              onChange={(e) => setRegiaoId(Number(e.target.value))}
            >
              <option value="">Escolha…</option>
              {(regioes ?? []).map((r) => (
                <option key={r.id} value={r.id}>
                  {r.nome}
                </option>
              ))}
            </select>
          </>
        )}

        <label className="np-rot">
          Posição no mapa (0 a 6
          {colecao === 'areas' ? ' — 0 é o hexágono central da região' : ''})
        </label>
        <select
          className="fi-sel"
          value={indiceMapa}
          onChange={(e) => setIndiceMapa(Number(e.target.value))}
        >
          {[0, 1, 2, 3, 4, 5, 6].map((i) => (
            <option key={i} value={i}>
              {i}
            </option>
          ))}
        </select>

        <p className="fi-modal-nota">
          Posição já ocupada é recusada pelo servidor. Quando o mapa existir, isto vira um
          clique no hexágono vazio.
        </p>

        <div className="fi-modal-btns">
          <button onClick={aoFechar}>Cancelar</button>
          <button
            disabled={!pronto}
            onClick={() =>
              aoCriar(
                colecao === 'regioes'
                  ? { nome: nome.trim(), indiceMapa }
                  : { nome: nome.trim(), indiceMapa, regiaoId }
              )
            }
          >
            Criar
          </button>
        </div>
      </div>
    </div>
  );
}

/** Índice da seção, com o que já foi preenchido em cada coleção. */
export function MundoInicio() {
  const navegar = useNavigate();
  return (
    <div className="mundo">
      {/*
        Esta era a última tela com cabeçalho próprio — `.eq-titulo` e `.eq-sub`, herdados
        de quando cada página escrevia o seu. Passar para o `TituloDaPagina` é o que faz o
        subtítulo de /mundo ter o mesmo tamanho do resto: com a classe antiga, mudar
        `.pg-sub` deixaria esta página para trás sem ninguém notar.

        `rotulo` explícito porque /mundo não tem item no menu — quem responde por ela é o
        apelido em `TITULOS`, e o componente só lê o menu.
      */}
      <TituloDaPagina rotulo="Criação do Mundo">
        A wiki de design do Mistik Island. Aqui o time escreve e cruza o mundo — não é o
        banco que roda o jogo.
      </TituloDaPagina>
      <div className="mu-secoes">
        {COLECOES.map((c) => (
          <button key={c.id} className="mu-secao" onClick={() => navegar(`/mundo/${c.id}`)}>
            <span className="mu-secao-ic">{c.icone}</span>
            <span className="mu-secao-nome">{c.rotulo}</span>
            <span className="mu-secao-desc">{c.descricao}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
